/**
 * O que faz: todas as leituras e ações da aba Agentes no Supabase (configuração, rotas, revisão, avisos, histórico).
 * Onde usa: hooks de useAgentes.ts.
 * Conexões: RPCs configurar_agentes_clinica, configure/remove_lead_classification_route, listar_revisao_agentes,
 *           contar_avisos_agentes, aprovar/recusar_sugestao_retorno, aceitar/dispensar_sugestao_etapa, desfazer_movimento_ia
 *           e a Edge Function run-lead-classification.
 * Tabelas: lead_classification_settings, lead_classification_routes e lead_stage_movements (SELECT com RLS por clínica).
 * Data: 25/09/2026.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  CONFIGURACAO_PADRAO,
  type AvisosAgentes,
  type CategoriaClassificacao,
  type ConfiguracaoAgentes,
  type ItemRevisao,
  type ModoRota,
  type MovimentoEtapa,
  type RotaMarco,
} from './contratos';

// Fachada tipada mínima: o arquivo de tipos gerado do Supabase não cobre as tabelas novas dos agentes.
interface RespostaBanco<T> { data: T | null; error: { message: string; code?: string } | null }
interface ConsultaBanco<T> extends PromiseLike<RespostaBanco<T>> {
  eq(coluna: string, valor: unknown): ConsultaBanco<T>;
  order(coluna: string, opcoes?: { ascending?: boolean }): ConsultaBanco<T>;
  limit(total: number): ConsultaBanco<T>;
  maybeSingle(): PromiseLike<RespostaBanco<T extends Array<infer U> ? U : T>>;
}
interface BancoAgentes {
  from(tabela: string): { select(colunas: string): ConsultaBanco<Array<Record<string, unknown>>> };
  rpc(funcao: string, argumentos: Record<string, unknown>): PromiseLike<RespostaBanco<unknown>>;
}
const banco = supabase as unknown as BancoAgentes;

function falhar(erro: { message: string } | null, contexto: string): void {
  // A mensagem técnica fica no console para diagnóstico; a tela traduz pelo código em mensagemErroAgentes.
  if (erro) {
    console.error(`[agentes] ${contexto}`, erro.message);
    throw new Error(erro.message);
  }
}

export async function carregarConfiguracao(clinicaId: string): Promise<{ configuracao: ConfiguracaoAgentes; rotas: RotaMarco[] }> {
  // Tabela: lead_classification_settings. Operação: SELECT da linha da clínica (RLS: própria clínica ou admin).
  // Campos: liga/desliga, IA, modos e prazos. Risco: nenhum dado de lead.
  const configuracaoResult = await banco.from('lead_classification_settings')
    .select('enabled, jev_enabled, agente_conversas_ativo, agente_retorno_ativo, agente_retorno_modo, prazo_retorno_padrao_dias, inactive_after_days, limite_espera_minutos, ia_pode_mover_colunas_meta')
    .eq('clinica_id', clinicaId)
    .maybeSingle();
  falhar(configuracaoResult.error, 'configuração');
  // Tabela: lead_classification_routes. Operação: SELECT do mapa marco → coluna da clínica.
  const rotasResult = await banco.from('lead_classification_routes')
    .select('category, etapa_kanban_id, mode')
    .eq('clinica_id', clinicaId);
  falhar(rotasResult.error, 'rotas');

  const linha = configuracaoResult.data;
  const configuracao: ConfiguracaoAgentes = linha ? {
    enabled: linha.enabled === true,
    iaHabilitada: linha.jev_enabled === true,
    agenteConversasAtivo: linha.enabled === true && linha.agente_conversas_ativo !== false,
    agenteRetornoAtivo: linha.agente_retorno_ativo === true,
    agenteRetornoModo: linha.agente_retorno_modo === 'automatic' ? 'automatic' : 'suggestion',
    prazoRetornoPadraoDias: Number(linha.prazo_retorno_padrao_dias ?? 30),
    inactiveAfterDays: Number(linha.inactive_after_days ?? 7),
    limiteEsperaMinutos: Number(linha.limite_espera_minutos ?? 30),
    iaPodeMoverColunasMeta: linha.ia_pode_mover_colunas_meta === true,
  } : CONFIGURACAO_PADRAO;
  const rotas = (rotasResult.data ?? []).map((rota) => ({
    categoria: String(rota.category) as CategoriaClassificacao,
    etapaId: String(rota.etapa_kanban_id),
    modo: (rota.mode === 'automatic' ? 'automatic' : 'suggestion') as ModoRota,
  }));
  return { configuracao, rotas };
}

export async function salvarConfiguracao(clinicaId: string, configuracao: ConfiguracaoAgentes, modoAdmin: boolean): Promise<void> {
  // RPC: configurar_agentes_clinica. Operação: UPSERT validado no servidor (tenant, plano PMK/Premium, prazos).
  const { error } = await banco.rpc('configurar_agentes_clinica', {
    p_agente_conversas_ativo: configuracao.agenteConversasAtivo,
    p_agente_retorno_ativo: configuracao.agenteRetornoAtivo,
    p_agente_retorno_modo: configuracao.agenteRetornoModo,
    p_prazo_retorno_padrao_dias: configuracao.prazoRetornoPadraoDias,
    p_ia_habilitada: configuracao.iaHabilitada,
    p_clinica_id: modoAdmin ? clinicaId : null,
    p_inactive_after_days: configuracao.inactiveAfterDays,
    p_limite_espera_minutos: configuracao.limiteEsperaMinutos,
  });
  falhar(error, 'salvar configuração');
}

export async function definirLiberacaoColunasMeta(clinicaId: string, liberar: boolean, modoAdmin: boolean): Promise<void> {
  // RPC: definir_liberacao_colunas_meta. Operação: UPDATE da autorização; ao desligar, o servidor volta
  // para sugestão as rotas automáticas que apontam para coluna de venda ou com evento Meta.
  const { error } = await banco.rpc('definir_liberacao_colunas_meta', { p_liberar: liberar, p_clinica_id: modoAdmin ? clinicaId : null });
  falhar(error, 'liberar colunas Meta');
}

export async function salvarRota(clinicaId: string, rota: RotaMarco, modoAdmin: boolean): Promise<void> {
  // RPC: configure_lead_classification_route. Operação: UPSERT do marco → coluna; o servidor bloqueia
  // modo automático em coluna de venda, retorno ou conversão Meta.
  const { error } = await banco.rpc('configure_lead_classification_route', {
    p_category: rota.categoria, p_stage_id: rota.etapaId, p_mode: rota.modo, p_clinica_id: modoAdmin ? clinicaId : null,
  });
  falhar(error, 'salvar rota');
}

export async function removerRota(clinicaId: string, categoria: CategoriaClassificacao, modoAdmin: boolean): Promise<void> {
  // RPC: remove_lead_classification_route. Operação: DELETE do mapa daquele marco na clínica.
  const { error } = await banco.rpc('remove_lead_classification_route', {
    p_category: categoria, p_clinica_id: modoAdmin ? clinicaId : null,
  });
  falhar(error, 'remover rota');
}

export async function listarRevisao(clinicaId: string, modoAdmin: boolean): Promise<ItemRevisao[]> {
  // RPC: listar_revisao_agentes. Operação: SELECT limitado a 50+50 itens por índice da clínica; sem conversa.
  // Desde a fase 3 também devolve critério, versão da regra e data do card das sugestões personalizadas.
  const { data, error } = await banco.rpc('listar_revisao_agentes', { p_clinica_id: modoAdmin ? clinicaId : null, p_limite: 50 });
  falhar(error, 'listar revisão');
  return ((data as Array<Record<string, unknown>> | null) ?? []).map((linha) => ({
    tipo: linha.tipo === 'retorno' ? 'retorno' : 'etapa',
    itemId: typeof linha.item_id === 'string' ? linha.item_id : null,
    leadId: String(linha.lead_id),
    leadNome: typeof linha.lead_nome === 'string' ? linha.lead_nome : null,
    etapaAtual: typeof linha.etapa_atual === 'string' ? linha.etapa_atual : null,
    etapaSugerida: typeof linha.etapa_sugerida === 'string' ? linha.etapa_sugerida : null,
    marco: typeof linha.marco === 'string' ? linha.marco as CategoriaClassificacao : null,
    motivo: typeof linha.motivo === 'string' ? linha.motivo : null,
    confianca: typeof linha.confianca === 'number' ? linha.confianca : linha.confianca == null ? null : Number(linha.confianca),
    agendarPara: typeof linha.agendar_para === 'string' ? linha.agendar_para : null,
    dataEstimada: typeof linha.data_estimada === 'boolean' ? linha.data_estimada : null,
    criadoEm: String(linha.criado_em),
    // Campos da organização personalizada; ausentes no banco antigo viram o comportamento legado.
    etapaSugeridaId: typeof linha.etapa_sugerida_id === 'string' ? linha.etapa_sugerida_id : null,
    personalizada: linha.personalizada === true,
    criterio: typeof linha.criterio === 'string' ? linha.criterio : null,
    regraVersao: typeof linha.regra_versao === 'string' ? linha.regra_versao : null,
    etapaAlteradaEm: typeof linha.etapa_alterada_em === 'string' ? linha.etapa_alterada_em : null,
  }));
}

export async function contarAvisos(clinicaId: string, modoAdmin: boolean): Promise<AvisosAgentes> {
  // RPC: contar_avisos_agentes. Operação: contagens agregadas por clínica; nenhuma linha de lead volta.
  const { data, error } = await banco.rpc('contar_avisos_agentes', { p_clinica_id: modoAdmin ? clinicaId : null });
  falhar(error, 'contar avisos');
  const avisos = (data ?? {}) as Record<string, unknown>;
  return {
    ativo: avisos.ativo === true,
    aguardandoEquipe: Number(avisos.aguardando_equipe ?? 0),
    aguardandoEquipeAtrasados: Number(avisos.aguardando_equipe_atrasados ?? 0),
    retornosParaRevisar: Number(avisos.retornos_para_revisar ?? 0),
    etapasParaRevisar: Number(avisos.etapas_para_revisar ?? 0),
    limiteEsperaMinutos: avisos.limite_espera_minutos == null ? null : Number(avisos.limite_espera_minutos),
  };
}

export async function aprovarRetorno(sugestaoId: string, agendarPara: string | null): Promise<void> {
  // RPC: aprovar_sugestao_retorno. Operação: cria o retorno pelas funções da tela de retornos (e move de coluna se configurado).
  const { error } = await banco.rpc('aprovar_sugestao_retorno', { p_sugestao_id: sugestaoId, p_agendar_para: agendarPara });
  falhar(error, 'aprovar retorno');
}

export async function recusarRetorno(sugestaoId: string): Promise<void> {
  // RPC: recusar_sugestao_retorno. Operação: UPDATE de estado; conta como erro do agente nas métricas.
  const { error } = await banco.rpc('recusar_sugestao_retorno', { p_sugestao_id: sugestaoId });
  falhar(error, 'recusar retorno');
}

export async function aceitarEtapa(clinicaId: string, leadId: string, modoAdmin: boolean): Promise<void> {
  // RPC: aceitar_sugestao_etapa. Operação: move o card como pessoa, sem pausar a IA do lead.
  const { error } = await banco.rpc('aceitar_sugestao_etapa', { p_lead_id: leadId, p_clinica_id: modoAdmin ? clinicaId : null });
  falhar(error, 'aceitar etapa');
}

export async function dispensarEtapa(clinicaId: string, leadId: string, modoAdmin: boolean): Promise<void> {
  // RPC: dispensar_sugestao_etapa. Operação: limpa a sugestão e registra a decisão da equipe.
  const { error } = await banco.rpc('dispensar_sugestao_etapa', { p_lead_id: leadId, p_clinica_id: modoAdmin ? clinicaId : null });
  falhar(error, 'dispensar etapa');
}

export async function listarMovimentos(clinicaId: string, leadId: string): Promise<MovimentoEtapa[]> {
  // Tabela: lead_stage_movements. Operação: SELECT dos 30 movimentos mais recentes de um lead (índice clinica/lead/data).
  // Risco: só IDs e códigos; nenhum texto de conversa.
  const { data, error } = await banco.from('lead_stage_movements')
    .select('id, etapa_origem_id, etapa_destino_id, ocorrido_em, executor, executor_origem, usuario_id, agente_chave, canal, motivo_codigo, confianca, modelo')
    .eq('clinica_id', clinicaId)
    .eq('lead_id', leadId)
    .order('ocorrido_em', { ascending: false })
    .limit(30);
  falhar(error, 'listar movimentos');
  return (data ?? []).map((linha) => ({
    id: String(linha.id),
    etapaOrigemId: typeof linha.etapa_origem_id === 'string' ? linha.etapa_origem_id : null,
    etapaDestinoId: typeof linha.etapa_destino_id === 'string' ? linha.etapa_destino_id : null,
    ocorridoEm: String(linha.ocorrido_em),
    executor: (['humano', 'ia', 'automacao'].includes(String(linha.executor)) ? linha.executor : 'desconhecido') as MovimentoEtapa['executor'],
    executorOrigem: linha.executor_origem === 'externo' ? 'externo' : linha.executor_origem === 'interno' ? 'interno' : null,
    usuarioId: typeof linha.usuario_id === 'string' ? linha.usuario_id : null,
    agenteChave: typeof linha.agente_chave === 'string' ? linha.agente_chave : null,
    canal: String(linha.canal),
    motivoCodigo: typeof linha.motivo_codigo === 'string' ? linha.motivo_codigo : null,
    confianca: linha.confianca == null ? null : Number(linha.confianca),
    modelo: typeof linha.modelo === 'string' ? linha.modelo : null,
  }));
}

export async function desfazerMovimento(movimentoId: string): Promise<void> {
  // RPC: desfazer_movimento_ia. Operação: volta o card para a coluna anterior e pausa a IA daquele lead.
  const { error } = await banco.rpc('desfazer_movimento_ia', { p_movimento_id: movimentoId });
  falhar(error, 'desfazer movimento');
}

export async function analisarAgora(clinicaId: string): Promise<{ processados: number; sugestoes: number; movidos: number; retornos: number }> {
  // Edge Function: run-lead-classification. Operação: reserva cota e processa até 50 leads da clínica agora.
  const { data, error } = await supabase.functions.invoke('run-lead-classification', { body: { clinicId: clinicaId } });
  if (error) {
    let mensagem = 'Não foi possível executar a análise agora. Tente novamente em alguns minutos.';
    try {
      const detalhes = await (error as { context?: Response }).context?.json();
      if (typeof detalhes?.error === 'string') mensagem = detalhes.error;
    } catch { /* Resposta não-JSON: mantém a mensagem segura. */ }
    throw new Error(mensagem);
  }
  const resultado = (data ?? {}) as Record<string, unknown>;
  return {
    processados: Number(resultado.processed ?? 0),
    sugestoes: Number(resultado.suggested ?? 0),
    movidos: Number(resultado.moved ?? 0),
    retornos: Number(resultado.returnSuggestions ?? 0),
  };
}
